import React from 'react'
import { useMutation } from '@apollo/client'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Form from 'antd/lib/form'
import Input from 'antd/lib/input'
import Button from 'antd/lib/button'
import message from 'antd/lib/message'
import Tooltip from 'antd/lib/tooltip'
import Divider from 'antd/lib/divider'
import { useSession } from '@slerp/accounts'
import { createCategory } from '../../Actions'
import { Store } from 'antd/lib/form/interface'
import { lightNoWhiteSpaceRule, noWhiteSpaceRule } from 'components/Login/rules'
import { QuestionCircleOutlined } from '@ant-design/icons'
import {
  UPDATE_MERCHANT_ARRANGEMENT_SETTINGS,
  CREATE_CATEGORY
} from '../../ProductQueries'
import '../../Products.css'
import { StyledFormItem } from '../commonStyles'
import { textAreaRule } from './rules'
import { useMerchant } from 'hooks/useGlobalContext'
import { MerchantCategory } from '@slerp/controls'

const { useForm } = Form

interface CategoryFormProps {
  merchantCategories: MerchantCategory[]
  onCancel?: () => void
  onStartSave?: () => void
  onEndSave?: () => void
}

const CategoryForm = (props: CategoryFormProps) => {
  const { onCancel, onStartSave, onEndSave } = props
  const [form] = useForm()
  const { merchant } = useSession()
  const {
    merchantData: { setting: merchantSettings }
  } = useMerchant()
  const [updateMerchantArrangementSettings] = useMutation(
    UPDATE_MERCHANT_ARRANGEMENT_SETTINGS
  )
  const [createMerchantCategory, { error: createCategoryError }] = useMutation(
    CREATE_CATEGORY,
    { refetchQueries: ['getMerchantCategories'] }
  )

  const initialValues = {
    name: '',
    description: null
  }

  const onFinish = (values: Store) => {
    const { name, description } = values

    if (merchantSettings) {
      form.validateFields().then(() => {
        onStartSave && onStartSave()
        return createCategory(
          merchant?.slug || '',
          name,
          description,
          merchantSettings,
          updateMerchantArrangementSettings,
          createMerchantCategory,
          () =>
            message.error(
              `Unable to create category due to: ${createCategoryError?.message}`,
              5
            )
        )
          .then((result) => {
            onEndSave && onEndSave()
          })
          .catch((error) => {
            message.error(
              `Unable to create category due to: ${error.message}`,
              5
            )
          })
      })
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      data-testid='category-form'
      labelCol={{ span: 24 }}
    >
      <Col span={24}>
        <Form.Item name='name' rules={noWhiteSpaceRule} label='Name'>
          <Input
            required
            placeholder='Type name here'
            data-testid='category-form-name'
          />
        </Form.Item>
      </Col>

      <Row className='_mb-0'>
        <Col span={24}>
          <StyledFormItem
            label={
              <Row className='_mb-0' align='middle'>
                <span className='_mr-8'>Description</span>
                <Tooltip
                  title={
                    <span>
                      Category descriptions can help customers better understand
                      what is on offer. They should be informative and
                      persuasive.
                      <br />
                      <br />
                      They can also help create more unique content on your
                      page, which boosts the SEO ranking of your shop page.
                    </span>
                  }
                >
                  <QuestionCircleOutlined className='_ml-8 _mr-8' />
                </Tooltip>
              </Row>
            }
            name='description'
            validateTrigger='onBlur'
            rules={[textAreaRule, ...lightNoWhiteSpaceRule]}
          >
            <Input.TextArea
              placeholder='Enter description here'
              data-testid='category-form-description'
              rows={2}
              maxLength={150}
              showCount={{
                formatter: ({ count, maxLength }) =>
                  `Remaining characters: ${(maxLength ?? 150) - count}`
              }}
            />
          </StyledFormItem>
        </Col>
      </Row>

      <Divider />

      <Row justify='space-between' className='-category-row'>
        <Button
          onClick={() => {
            form.resetFields()
            onCancel && onCancel()
          }}
        >
          Discard
        </Button>
        <Button
          type='primary'
          htmlType='submit'
          data-testid='category-form-add-category'
        >
          Save
        </Button>
      </Row>
    </Form>
  )
}

export default CategoryForm
